import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Info, Loader2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMessage } from "@/lib/api";

const EMPTY = { name: "", telegram: "", email: "", account_count: "", notes: "" };

export const PurchaseEnquiryModal = ({ open, onOpenChange, plan, config }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setErrors({});
      setDone(null);
    }
  }, [open, plan?.slug]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Name at least 2 letters ya.";
    if (form.telegram.trim().length < 2)
      next.telegram = "Telegram handle is how we confirm payment details.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await api.createPurchaseIntent({
        plan_slug: plan.slug,
        name: form.name.trim(),
        telegram: form.telegram.trim(),
        email: form.email.trim() || null,
        account_count: form.account_count.trim() || null,
        notes: form.notes.trim() || null,
      });
      setDone(res);
      toast.success(`Request received. Ref ${res.ref_code}`);
    } catch (error) {
      toast.error(errMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const price = plan ? `${plan.price_label} ${plan.currency}`.trim() : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="purchase-enquiry-modal"
        className="max-h-[92vh] overflow-y-auto sm:max-w-lg"
        style={{ background: "var(--gm-navy-925)", borderColor: "rgba(255,255,255,0.14)" }}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white">
            Request: {plan?.name}
          </DialogTitle>
          <DialogDescription style={{ color: "var(--gm-muted)" }}>
            {price ? `${price} \u00b7 ` : ""}Enquiry only - no payment is taken on this site.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex gap-2.5 rounded-xl p-3"
          style={{
            background: "rgba(76,195,255,0.07)",
            border: "1px solid rgba(76,195,255,0.25)",
          }}
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--gm-info)" }} />
          <p className="text-xs leading-relaxed" style={{ color: "var(--gm-muted)" }}>
            Submit this and we message you on Telegram with the payment details and
            licence steps. No card form, no auto-checkout here.
          </p>
        </div>

        {done ? (
          <div className="space-y-4" data-testid="purchase-success">
            <div
              className="rounded-xl p-4"
              style={{
                background: "rgba(43,213,118,0.08)",
                border: "1px solid rgba(43,213,118,0.28)",
              }}
            >
              <p className="text-sm font-semibold text-white">{done.message}</p>
              <p className="mt-2 text-xs" style={{ color: "var(--gm-muted)" }}>
                Your reference
              </p>
              <p
                className="font-mono-gm text-lg font-semibold"
                data-testid="purchase-success-code"
                style={{ color: "var(--gm-success)" }}
              >
                {done.ref_code}
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <a
                href={config?.telegram_owner || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="gm-btn-red flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl text-sm font-bold"
                data-testid="purchase-success-telegram"
              >
                <Send className="h-4 w-4" /> Continue on Telegram
              </a>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="purchase-close-button"
                className="gm-btn-ghost min-h-[44px] rounded-xl px-4 text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="purchase-form" noValidate>
            <div>
              <Label htmlFor="pi-name" className="text-white">
                Name
              </Label>
              <Input
                id="pi-name"
                value={form.name}
                onChange={set("name")}
                data-testid="purchase-name-input"
                className="mt-1.5"
                placeholder="e.g. Amir"
              />
              {errors.name ? (
                <p className="mt-1 text-xs" style={{ color: "var(--gm-danger)" }}>
                  {errors.name}
                </p>
              ) : null}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="pi-tg" className="text-white">
                  Telegram
                </Label>
                <Input
                  id="pi-tg"
                  value={form.telegram}
                  onChange={set("telegram")}
                  data-testid="purchase-telegram-input"
                  className="mt-1.5"
                  placeholder="@yourhandle"
                />
                {errors.telegram ? (
                  <p className="mt-1 text-xs" style={{ color: "var(--gm-danger)" }}>
                    {errors.telegram}
                  </p>
                ) : null}
              </div>
              <div>
                <Label htmlFor="pi-email" className="text-white">
                  Email <span style={{ color: "var(--gm-muted-2)" }}>(optional)</span>
                </Label>
                <Input
                  id="pi-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="purchase-email-input"
                  className="mt-1.5"
                  placeholder="you@email.com"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="pi-count" className="text-white">
                How many accounts?{" "}
                <span style={{ color: "var(--gm-muted-2)" }}>(optional)</span>
              </Label>
              <Input
                id="pi-count"
                value={form.account_count}
                onChange={set("account_count")}
                data-testid="purchase-count-input"
                className="mt-1.5"
                placeholder="e.g. 3"
              />
            </div>
            <div>
              <Label htmlFor="pi-notes" className="text-white">
                Notes <span style={{ color: "var(--gm-muted-2)" }}>(optional)</span>
              </Label>
              <Textarea
                id="pi-notes"
                rows={3}
                value={form.notes}
                onChange={set("notes")}
                data-testid="purchase-notes-input"
                className="mt-1.5"
                placeholder="Broker, leverage, anything we should know"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="purchase-submit-button"
              className="gm-btn-red flex min-h-[46px] w-full items-center justify-center gap-2 rounded-xl text-sm font-bold disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {busy ? "Sending..." : "Send request"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
